import { boolean, discriminatedUnion, literal, number, object, preprocess, string } from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { AwardType } from '@occc-app/offerHelpers/types';
import {
  AwardPointsSchema,
  AwardVouchersWithExpirationDaysSchema,
  CustomDataSchema,
  OfferNameAndDescriptionSchema,
  VoucheredOfferSchema,
  withIsEnabled,
} from '@occc/validation';
import {
  fulfillmentWindowSchema,
  requiresActivationConstraintWithLimitsSchema,
} from '@occc/feat-constraints/schemas';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';

export const EVENT_TYPE_WHITESPACE_ERROR = 'Loyalty Event name should not include whitespaces';

const LoyaltyEventSchema = object({
  id: string().optional(),
  eventType: string({ required_error: IS_REQUIRED }).regex(/^\S+$/, EVENT_TYPE_WHITESPACE_ERROR),
});

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({ membership: data.membership, global: data.global, ctx });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
});

const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

const BaseLoyaltyEventSchema = OfferNameAndDescriptionSchema.extend({
  earningMechanism: object({ id: string().optional() }),
  loyaltyEvent: preprocess((value) => value ?? {}, LoyaltyEventSchema),
  awardCount: AwardCountSchema,
  activation: requiresActivationConstraintWithLimitsSchema,
  fulfillmentWindow: fulfillmentWindowSchema,
  customData: CustomDataSchema,
  visibleForCommandCenterFulfillment: boolean(),

  awardPoints: AwardPointsSchema.partial().optional(),
  awardVouchers: object({
    voucheredOffer: VoucheredOfferSchema.partial().passthrough().optional(),
    isEnabled: boolean().optional(),
    voucherCount: number().optional(),
    voucherExpirationDays: number().optional(),
  })
    .partial()
    .optional(),
});

const AwardNullableVariantSchema = object({ awardType: literal(AwardType.AwardNullable) });

const AwardPointsVariantSchema = object({
  awardType: literal(AwardType.AwardPoints),
  awardPoints: preprocess((value) => value ?? {}, AwardPointsSchema),
});

const AwardVouchersVariantSchema = object({
  awardType: literal(AwardType.AwardVouchers),
  awardVouchers: preprocess((value) => value ?? {}, AwardVouchersWithExpirationDaysSchema),
});

export const LoyaltyEventConfigurationSchema = discriminatedUnion('awardType', [
  BaseLoyaltyEventSchema.merge(AwardNullableVariantSchema),
  BaseLoyaltyEventSchema.merge(AwardPointsVariantSchema),
  BaseLoyaltyEventSchema.merge(AwardVouchersVariantSchema),
]);

export const validateConfiguration = makeValidate(LoyaltyEventConfigurationSchema);
